import React from 'react';

import { FormattedMessage, useIntl } from '../../../../util/reactIntl';

import { NamedLink } from '../../../../components';

import css from './SectionAboutBanner.module.css';

const PHOTO_2014_SRC = '/static/images/landing/om-oss-2014.avif';
const PHOTO_2026_SRC = '/static/images/landing/om-oss-2026.avif';

const IconArrow = () => (
  <svg
    className={css.arrow}
    width="22"
    height="12"
    viewBox="0 0 22 12"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M1 6h19M15.5 1.5 20 6l-4.5 4.5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * A photograph as a print: white border, a year written underneath, set at a
 * slight angle.
 *
 * @param {Object} props
 * @param {string} props.className - size, angle and stacking of this print
 * @param {string} props.src
 * @param {string} props.alt
 * @param {string} props.year
 * @returns {JSX.Element}
 */
const Print = ({ className, src, alt, year }) => (
  <figure className={className}>
    <img className={css.printImage} src={src} alt={alt} loading="lazy" />
    <figcaption className={css.printYear}>{year}</figcaption>
  </figure>
);

/**
 * "Fra låvemarked til Zuprize": the homepage's way into the About page
 * (Guro's teaser, September 2026). The homepage promises a story; Om oss
 * tells it. Two prints, the barn market in 2014 and Guro at her wardrobe in
 * 2026, beside the copy on desktop and above it on a phone.
 *
 * Built as real text and real photographs rather than the flat banner image
 * she supplied as the design, so the type stays sharp at every width, reads
 * in the site's own fonts, and is there for screen readers and search.
 *
 * @returns {JSX.Element}
 */
const SectionAboutBanner = () => {
  const intl = useIntl();

  return (
    <section className={css.root}>
      <div className={css.inner}>
        <div className={css.text}>
          <p className={css.eyebrow}>
            <FormattedMessage id="SectionAboutBanner.eyebrow" />
          </p>
          <h2 className={css.title}>
            <FormattedMessage id="SectionAboutBanner.title" values={{ br: <br /> }} />
          </h2>
          <p className={css.subtitle}>
            <FormattedMessage id="SectionAboutBanner.subtitle" />
          </p>
          <p className={css.body}>
            <FormattedMessage id="SectionAboutBanner.body" />
          </p>
          <NamedLink className={css.cta} name="AboutPage">
            <FormattedMessage id="SectionAboutBanner.cta" />
            <IconArrow />
          </NamedLink>
        </div>

        <div className={css.prints}>
          <Print
            className={css.printThen}
            src={PHOTO_2014_SRC}
            alt={intl.formatMessage({ id: 'SectionAboutBanner.photo2014Alt' })}
            year="2014"
          />
          <Print
            className={css.printNow}
            src={PHOTO_2026_SRC}
            alt={intl.formatMessage({ id: 'SectionAboutBanner.photo2026Alt' })}
            year="2026"
          />
        </div>
      </div>
    </section>
  );
};

export default SectionAboutBanner;
